import React, {useEffect, useMemo, useRef, useState} from "react";
import {ExportOutlined} from "@ant-design/icons";
import {Input, Modal, Radio, Spin, Table, message} from "antd";
import {LeadStatsService} from "../../../../../shared/api/leadStats/leadStats.service";
import "./LeadStatsSection.scss";

// «Эффективность по лидам»: a funnel of Bitrix by employee: how far their leads went, the time to the first call and
// talk, the leads with no talk. The server works it out (backend service/leadStats.service.js) from
// copies of Bitrix's data. A click on an employee shows the funnel and the list for them alone, a click on a number the
// deals behind it. The numbers of the view «Что с ними стало» and all the popups count report.leads here, so they agree.

const BITRIX_DEAL = 'https://eci-spb.bitrix24.ru/crm/deal/details'

// a date in Moscow as 'YYYY-MM-DD'
const dayOf = (date) => date.toLocaleDateString('sv-SE', {timeZone: 'Europe/Moscow'})
const today = () => dayOf(new Date())
const shifted = (days) => dayOf(new Date(Date.now() - days * 24 * 60 * 60 * 1000))
const firstOfMonth = (day) => `${day.slice(0, 8)}01`
const previousMonth = () => {
  const lastDay = new Date(`${firstOfMonth(today())}T12:00:00Z`)
  lastDay.setUTCDate(0)
  const to = lastDay.toISOString().slice(0, 10)
  return {from: firstOfMonth(to), to}
}
const monday = () => {
  const weekday = (new Date(`${today()}T12:00:00Z`).getUTCDay() + 6) % 7
  return shifted(weekday)
}

const PERIODS = [
  {key: 'week', title: 'Эта неделя', range: () => ({from: monday(), to: today()})},
  {key: 'month', title: 'Этот месяц', range: () => ({from: firstOfMonth(today()), to: today()})},
  {key: 'previous', title: 'Прошлый месяц', range: previousMonth},
  {key: 'days30', title: '30 дней', range: () => ({from: shifted(29), to: today()})},
  {key: 'days90', title: '90 дней', range: () => ({from: shifted(89), to: today()})},
]

const FUNNELS = [
  {id: 14, title: 'Прямой покупатель'},
  {id: 10, title: 'Агент-покупатель (ДОП)'},
]

const STUCK_DAYS = [1, 3, 7, 14, 30]

const percent = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0)
const momentOf = (at) => new Date(at).toLocaleString('ru-RU', {timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}).replace(',', '')
const timeOf = (minutes) => {
  if (minutes === null || minutes === undefined) return '—'
  if (minutes === 0) return 'сразу'
  if (minutes < 60) return `${minutes} мин`
  const hours = Math.floor(minutes / 60)
  return hours < 10 && minutes % 60 ? `${hours} ч ${minutes % 60} мин` : `${hours} ч`
}

// what the numbers of the report count
const FITS = {
  all: () => true,
  open: (lead) => lead.open,
  goal: (lead) => lead.goal,
  called: (lead) => lead.known && lead.contacted,
  talked: (lead) => lead.known && lead.toTalk !== null,
  noCall: (lead) => lead.known && !lead.contacted,
  noTalk: (lead) => lead.known && lead.toTalk === null,
  noTalkOpen: (lead) => lead.known && lead.toTalk === null && lead.open,
  toCall: (lead) => lead.toCall !== null,
  toTalk: (lead) => lead.toTalk !== null,
}

// what became of the leads that came to a stage: every one of them is in one of these
const FLOW = [
  {key: 'next', title: 'Дальше', color: '#0966aa', fits: (lead, stage) => (lead.stopped === stage ? lead.outcome === 'won' : lead.reached > stage)},
  {key: 'here', title: 'На стадии', color: '#1baf7a', fits: (lead, stage) => lead.stopped === stage && lead.outcome === 'open'},
  {key: 'back', title: 'Вернули', color: '#eda100', fits: (lead, stage) => lead.stopped < stage && lead.reached === stage},
  {key: 'lost', title: 'Проиграны', color: '#c0392b', fits: (lead, stage) => lead.stopped === stage && lead.outcome === 'lost'},
  {key: 'handed', title: 'Переданы', color: '#6250d6', fits: (lead, stage) => lead.stopped === stage && lead.outcome === 'handed'},
]

// a number that opens its deals; without onClick, only the number
const Num = ({children, onClick}) => (onClick
  ? <button type='button' className='lead-stats__num' onClick={onClick}>{children}</button>
  : <>{children}</>)

const daysOf = (days) => {
  const last = days % 10
  const teen = days % 100 >= 11 && days % 100 <= 14
  if (teen || last === 0 || last >= 5) return `${days} дней`
  return `${days} ${last === 1 ? 'день' : 'дня'}`
}

const Tile = ({title, value, note, alert, onClick}) => (
  <div className={`lead-stats__tile${alert ? ' lead-stats__tile--alert' : ''}`}>
    <span>{title}</span>
    <b><Num onClick={onClick}>{value}</Num></b>
    <small>{note || ' '}</small>
  </div>
)

const Share = ({part, whole, onClick}) => (
  <div className='lead-stats__share'>
    <span><Num onClick={part ? onClick : undefined}>{part}</Num> <i>{percent(part, whole)}%</i></span>
    <div><div style={{width: `${percent(part, whole)}%`}}/></div>
  </div>
)

const Funnel = ({stages, row, show}) => (
  <div className='lead-stats__funnel'>
    {stages.map((stage, index) => {
      const count = row.reached[index]
      const previous = index ? row.reached[index - 1] : count
      return (
        <div className='lead-stats__step' key={stage.id}>
          <span className='lead-stats__step-name' title={stage.name}>{stage.name}</span>
          <div className='lead-stats__step-bar'><div style={{width: `${percent(count, row.leads)}%`}}/></div>
          <b><Num onClick={count ? show(stage.name, (lead) => lead.reached >= index) : undefined}>{count}</Num></b>
          <i>{percent(count, row.leads)}%</i>
          <small>{index && previous ? `${percent(count, previous)}% от прошлой` : ''}</small>
        </div>
      )
    })}
  </div>
)

const Flow = ({stages, leads, show}) => (
  <div className='lead-stats__flow'>
    <div className='lead-stats__flow-table'>
      <div className='lead-stats__flow-row lead-stats__flow-row--head'>
        <span>Стадия</span>
        <b>Всего</b>
        <span/>
        {FLOW.map((part) => <span className='lead-stats__flow-key' key={part.key}><i style={{background: part.color}}/>{part.title}</span>)}
      </div>
      {stages.map((stage, index) => {
        const came = leads.filter((lead) => lead.reached >= index)
        const parts = FLOW.map((part) => ({...part, count: came.filter((lead) => part.fits(lead, index)).length}))
        const open = (part) => show(`${stage.name}: ${part.title.toLowerCase()}`, (lead) => part.fits(lead, index))
        return (
          <div className='lead-stats__flow-row' key={stage.id}>
            <span className='lead-stats__step-name' title={stage.name}>{stage.name}</span>
            <b><Num onClick={came.length ? show(stage.name, (lead) => lead.reached >= index) : undefined}>{came.length}</Num></b>
            <div className='lead-stats__flow-bar'>
              {parts.filter((part) => part.count).map((part) => (
                <button type='button' key={part.key} style={{flexGrow: part.count, background: part.color}} onClick={open(part)}
                        title={`${part.title}: ${part.count} из ${came.length}, ${percent(part.count, came.length)}%`}/>
              ))}
            </div>
            {parts.map((part) => <span key={part.key}><Num onClick={part.count ? open(part) : undefined}>{part.count || '·'}</Num></span>)}
          </div>
        )
      })}
    </div>
  </div>
)

const StageCell = ({stage, count, whole, peak, inPercent, onClick}) => (
  <td style={{background: count ? `rgba(9, 102, 170, ${0.06 + (count / (inPercent ? whole : peak)) * 0.5})` : undefined}}
      title={`${stage.name}: ${count} из ${whole}`}>
    <Num onClick={count ? onClick : undefined}>{inPercent ? whole > 0 && `${percent(count, whole)}%` : count || ''}</Num>
  </td>
)

const employeeColumns = (show, goalTitle) => [
  {title: 'Сотрудник', dataIndex: 'name', fixed: 'left', width: 170, render: (name) => <b>{name}</b>},
  {title: 'Лидов', dataIndex: 'leads', width: 80, align: 'right', sorter: (a, b) => a.leads - b.leads, defaultSortOrder: 'descend',
    render: (count, row) => <Num onClick={show('Лиды', FITS.all, row)}>{count}</Num>},
  {title: 'Был звонок', dataIndex: 'called', width: 120, sorter: (a, b) => a.called - b.called, render: (called, row) => <Share part={called} whole={row.leads - row.noPhone} onClick={show('Был звонок', FITS.called, row)}/>},
  {title: 'Был разговор', dataIndex: 'talked', width: 120, sorter: (a, b) => a.talked - b.talked, render: (talked, row) => <Share part={talked} whole={row.leads - row.noPhone} onClick={show('Был разговор', FITS.talked, row)}/>},
  {title: 'Без звонка', dataIndex: 'noCall', width: 90, align: 'right', sorter: (a, b) => a.noCall - b.noCall, render: (count, row) => (count ? <b className='lead-stats__bad'><Num onClick={show('Без звонка', FITS.noCall, row)}>{count}</Num></b> : 0)},
  {title: 'Без разговора', dataIndex: 'noTalk', width: 100, align: 'right', sorter: (a, b) => a.noTalk - b.noTalk, render: (count, row) => (count ? <b className='lead-stats__bad'><Num onClick={show('Без разговора', FITS.noTalk, row)}>{count}</Num></b> : 0)},
  {title: 'До первого звонка', dataIndex: 'toCall', width: 110, align: 'right', sorter: (a, b) => (a.toCall ?? Infinity) - (b.toCall ?? Infinity),
    render: (minutes, row) => <Num onClick={minutes === null ? undefined : show('До первого звонка', FITS.toCall, row, TIMES.toCall)}>{timeOf(minutes)}</Num>},
  {title: 'До разговора', dataIndex: 'toTalk', width: 110, align: 'right', sorter: (a, b) => (a.toTalk ?? Infinity) - (b.toTalk ?? Infinity),
    render: (minutes, row) => <Num onClick={minutes === null ? undefined : show('До разговора', FITS.toTalk, row, TIMES.toTalk)}>{timeOf(minutes)}</Num>},
  {title: goalTitle, dataIndex: 'goal', width: 100, align: 'right', sorter: (a, b) => a.goal - b.goal,
    render: (count, row) => <Num onClick={count ? show(goalTitle, FITS.goal, row) : undefined}>{count}</Num>},
]

// a popup's own last column, it is sorted by it
const timeColumn = (title, key, render = timeOf) => ({title, dataIndex: key, width: 110, align: 'right', sorter: (a, b) => a[key] - b[key], defaultSortOrder: 'descend', render})

const TIMES = {
  toCall: [timeColumn('До звонка', 'toCall')],
  toTalk: [timeColumn('До разговора', 'toTalk')],
}

const STUCK_COLUMNS = [
  {title: 'Последний звонок', dataIndex: 'calledAt', width: 120, sorter: (a, b) => new Date(a.calledAt || 0) - new Date(b.calledAt || 0),
    render: (at) => (at ? momentOf(at) : <b className='lead-stats__bad'>Не звонили</b>)},
  timeColumn('Без движения', 'idleDays', daysOf),
]

const dealLink = (id, row) => (
  <>
    <a href={`${BITRIX_DEAL}/${id}/`} target='_blank' rel='noopener noreferrer'>{id} <ExportOutlined/></a>
    {row.repeats > 0 && <span className='lead-stats__muted' title='Ещё сделок с этим телефоном'> +{row.repeats}</span>}
  </>
)

const byText = (key) => (a, b) => String(a[key]).localeCompare(String(b[key]), 'ru')
const popupColumns = (extra = []) => [
  {title: 'Сделка', dataIndex: 'id', width: 120, render: dealLink},
  {title: 'Название', dataIndex: 'title', ellipsis: {showTitle: true}, render: (title, row) => title || `Сделка ${row.id}`},
  {title: 'Стадия', dataIndex: 'stage', width: 190, sorter: byText('stage'), render: (stage, row) => <span className={row.open ? '' : 'lead-stats__muted'}>{stage}</span>},
  {title: 'Сотрудник', dataIndex: 'owner', width: 170, sorter: byText('owner')},
  {title: 'Создана', dataIndex: 'createdAt', width: 110, sorter: byText('createdAt'), defaultSortOrder: extra.length ? undefined : 'descend', render: momentOf},
  ...extra,
]

const listColumns = [
  {title: 'Сделка', dataIndex: 'id', width: 110, render: dealLink},
  {title: 'Создана', dataIndex: 'createdAt', width: 120, render: momentOf},
  {title: 'Сотрудник', dataIndex: 'owner'},
  {title: 'Стадия', dataIndex: 'stage', render: (stage, row) => <span className={row.open ? '' : 'lead-stats__muted'}>{stage}</span>},
  {title: 'Звонок', dataIndex: 'contacted', width: 130, render: (called) => (called ? 'Звонили, не дозвонились' : <b className='lead-stats__bad'>Не звонили</b>)},
  {title: 'Прошло', dataIndex: 'waiting', width: 100, align: 'right', render: timeOf},
]

const LIST_FILTERS = [
  {key: 'open', title: 'В работе', fits: (deal) => deal.open},
  {key: 'closed', title: 'Закрытые', fits: (deal) => !deal.open},
  {key: 'all', title: 'Все', fits: () => true},
]

const FUNNEL_VIEWS = [
  {key: 'reached', title: 'Сколько дошло'},
  {key: 'flow', title: 'Что с ними стало'},
]

const MATRIX_VIEWS = [
  {key: 'count', title: 'Числа'},
  {key: 'percent', title: 'Проценты'},
]

const MATRIX_BASES = [
  {key: 'leads', title: 'От всех лидов'},
  {key: 'previous', title: 'От прошлой стадии'},
]

export const LeadStatsSection = () => {
  const [funnel, setFunnel] = useState(FUNNELS[0].id)
  const [period, setPeriod] = useState('days30')
  const [dates, setDates] = useState(PERIODS[3].range())
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)
  const [employeeId, setEmployeeId] = useState(null)
  const [listFilter, setListFilter] = useState('open')
  const [matrixView, setMatrixView] = useState('count')
  const [matrixBase, setMatrixBase] = useState('leads')
  const [funnelView, setFunnelView] = useState('reached')
  const [stuckDays, setStuckDays] = useState(3)
  const [popup, setPopup] = useState({visible: false, title: '', who: '', deals: []})
  const asked = useRef(0)

  const load = async (range, funnelId = funnel) => {
    if (!range.from || !range.to) return
    asked.current += 1
    const request = asked.current
    setLoading(true)
    try {
      const {data} = await LeadStatsService.fetchReport({...range, funnel: funnelId})
      if (request === asked.current) setReport(data)
    } catch (error) {
      if (request === asked.current) message.error(error?.response?.data?.errors?.message || 'Не удалось построить отчёт')
    } finally {
      if (request === asked.current) setLoading(false)
    }
  }

  useEffect(() => {
    load(dates)
    // eslint-disable-next-line
  }, [])

  const chooseFunnel = ({target}) => {
    setFunnel(target.value)
    setEmployeeId(null)
    load(dates, target.value)
  }
  const choosePeriod = ({target}) => {
    const range = PERIODS.find((item) => item.key === target.value).range()
    setPeriod(target.value)
    setDates(range)
    load(range)
  }
  const chooseDay = (key) => ({target}) => {
    const range = {...dates, [key]: target.value}
    setPeriod(null)
    setDates(range)
    load(range)
  }

  const employee = report && report.employees.find((row) => row.id === employeeId)
  const row = employee || (report && report.total)
  const leads = useMemo(() => {
    if (!report) return []
    return employee ? report.leads.filter((lead) => lead.ownerId === employee.id) : report.leads
  }, [report, employee])
  const list = useMemo(() => {
    const fits = LIST_FILTERS.find((item) => item.key === listFilter).fits
    return leads.filter((lead) => FITS.noTalk(lead) && fits(lead))
      .sort((a, b) => Number(b.open) - Number(a.open) || b.waiting - a.waiting)
  }, [leads, listFilter])
  // the deals of a number, of one employee or of those on the screen
  const show = (title, fits, owner = employee, extra) => (event) => {
    event.stopPropagation()
    const deals = report.leads.filter((lead) => (!owner || lead.ownerId === owner.id) && fits(lead))
    setPopup({visible: true, title, who: owner ? owner.name : 'Все сотрудники', deals, extra})
  }
  const stuck = useMemo(() => {
    if (!report) return []
    return report.stuck.filter((lead) => lead.idleDays >= stuckDays && (!employee || lead.ownerId === employee.id))
  }, [report, employee, stuckDays])
  const stuckStages = report ? report.stages
    .map((stage, index) => ({...stage, deals: stuck.filter((lead) => lead.stopped === index)}))
    .filter((stage) => stage.deals.length) : []
  const showStuck = (title, deals) => () => setPopup({
    visible: true, title: `${title}: без движения ${daysOf(stuckDays)}+`, who: employee ? employee.name : 'Все сотрудники', deals, extra: STUCK_COLUMNS,
  })
  const columns = useMemo(() => employeeColumns(show, report ? report.goalTitle : ''), [report, employee]) // eslint-disable-line
  const counted = (items, key) => (report ? report[items]
    .map((item) => ({...item, count: row[key][item.id] || 0}))
    .filter((item) => item.count) : [])
  const closed = counted('closed', 'closed')
  const handed = counted('handed', 'handed')
  const peak = report ? Math.max(1, ...report.employees.flatMap((item) => item.reached)) : 1
  // the first stage is what the percents count from, so it stays a number
  const inPercent = (index) => matrixView === 'percent' && index > 0

  return (
    <div className='lead-stats'>
      <div className='lead-stats__bar'>
        <Radio.Group value={funnel} onChange={chooseFunnel} buttonStyle='solid'>
          {FUNNELS.map((item) => <Radio.Button key={item.id} value={item.id}>{item.title}</Radio.Button>)}
        </Radio.Group>
      </div>
      <div className='lead-stats__bar'>
        <Radio.Group value={period} onChange={choosePeriod} buttonStyle='solid'>
          {PERIODS.map((item) => <Radio.Button key={item.key} value={item.key}>{item.title}</Radio.Button>)}
        </Radio.Group>
        <div className='lead-stats__dates'>
          <Input type='date' value={dates.from} min={report ? report.dataFrom : undefined} onChange={chooseDay('from')} aria-label='С даты'/>
          <span>—</span>
          <Input type='date' value={dates.to} min={report ? report.dataFrom : undefined} onChange={chooseDay('to')} aria-label='По дату'/>
        </div>
        {report && report.syncedAt && <span className='lead-stats__muted'>Данные из Битрикса на {momentOf(report.syncedAt)}</span>}
      </div>

      {!report && <Spin/>}
      {report && !report.total.leads && !loading && <p>За эти даты лидов нет.</p>}
      {report && report.total.leads > 0 && (
        <Spin spinning={loading}>
          <div className='lead-stats__who'>
            <h2>{employee ? employee.name : 'Все сотрудники'}</h2>
            {employee && <button type='button' onClick={() => setEmployeeId(null)}>Показать всех</button>}
          </div>
          <div className='lead-stats__tiles'>
            <Tile title='Лидов' value={row.leads} onClick={show('Лиды', FITS.all)}
                  note={<>в работе <Num onClick={row.open ? show('В работе', FITS.open) : undefined}>{row.open}</Num>, {report.goalTitle.toLowerCase()} <Num onClick={row.goal ? show(report.goalTitle, FITS.goal) : undefined}>{row.goal}</Num></>}/>
            <Tile title='Был разговор' value={`${percent(row.talked, row.leads - row.noPhone)}%`} note={`${row.talked} из ${row.leads - row.noPhone}`}
                  onClick={row.talked ? show('Был разговор', FITS.talked) : undefined}/>
            <Tile title='Без разговора' value={row.noTalk} alert={row.noTalkOpen > 0} onClick={row.noTalk ? show('Без разговора', FITS.noTalk) : undefined}
                  note={<>из них в работе <Num onClick={row.noTalkOpen ? show('Без разговора, в работе', FITS.noTalkOpen) : undefined}>{row.noTalkOpen}</Num></>}/>
            <Tile title='Без звонка' value={row.noCall} note='ни одного звонка' alert={row.noCall > 0} onClick={row.noCall ? show('Без звонка', FITS.noCall) : undefined}/>
            <Tile title='До первого звонка' value={timeOf(row.toCall)} note='медиана, рабочее время'
                  onClick={row.toCall === null ? undefined : show('До первого звонка', FITS.toCall, employee, TIMES.toCall)}/>
            <Tile title='До разговора' value={timeOf(row.toTalk)} note='медиана, рабочее время'
                  onClick={row.toTalk === null ? undefined : show('До разговора', FITS.toTalk, employee, TIMES.toTalk)}/>
          </div>

          <div className='lead-stats__list-head'>
            <h3>Зависшие лиды</h3>
            <Radio.Group size='small' value={stuckDays} onChange={({target}) => setStuckDays(target.value)}>
              {STUCK_DAYS.map((days) => <Radio.Button key={days} value={days}>{daysOf(days)}</Radio.Button>)}
            </Radio.Group>
          </div>
          <p className='lead-stats__hint'>
            Открытые сделки, где за этот срок не было ни звонка, ни смены стадии. Показаны все, даты отчёта на них не влияют.
            {report.byFirstOwner && ' Сотрудник здесь — текущий ответственный, ему и звонить.'}
          </p>
          {stuck.length > 0 ? (
            <div className='lead-stats__stuck'>
              <button type='button' className='lead-stats__stuck-card lead-stats__stuck-card--all' onClick={showStuck('Все зависшие', stuck)}>
                <span>Все зависшие</span>
                <b>{stuck.length}</b>
              </button>
              {stuckStages.map((stage) => (
                <button type='button' className='lead-stats__stuck-card' key={stage.id} onClick={showStuck(stage.name, stage.deals)}>
                  <span>{stage.name}</span>
                  <b>{stage.deals.length}</b>
                </button>
              ))}
            </div>
          ) : <p className='lead-stats__muted'>Зависших лидов нет.</p>}

          <h3>Сотрудники</h3>
          <Table className='lead-stats__table' size='small' rowKey='id' columns={columns} dataSource={report.employees}
                 pagination={false} sticky scroll={{x: 1000}} showSorterTooltip={false}
                 rowClassName={(item) => `lead-stats__row${item.id === employeeId ? ' lead-stats__row--active' : ''}`}
                 onRow={(item) => ({onClick: () => setEmployeeId(item.id === employeeId ? null : item.id)})}/>

          <div className={`lead-stats__columns${funnelView === 'flow' ? ' lead-stats__columns--wide' : ''}`}>
            <section>
              <div className='lead-stats__list-head'>
                <h3>Воронка</h3>
                <Radio.Group size='small' value={funnelView} onChange={({target}) => setFunnelView(target.value)}>
                  {FUNNEL_VIEWS.map((item) => <Radio.Button key={item.key} value={item.key}>{item.title}</Radio.Button>)}
                </Radio.Group>
              </div>
              {funnelView === 'reached'
                ? <Funnel stages={report.stages} row={row} show={show}/>
                : <Flow stages={report.stages} leads={leads} show={show}/>}
            </section>
            <section>
              <h3>Закрыты без сделки</h3>
              {closed.length ? closed.map((stage) => (
                <div className='lead-stats__closed' key={stage.id}>
                  <span>{stage.name}</span>
                  <b><Num onClick={show(stage.name, (lead) => lead.outcome === 'lost' && lead.stageId === stage.id)}>{stage.count}</Num></b>
                </div>
              )) : <p className='lead-stats__muted'>Таких лидов нет.</p>}
              {handed.length > 0 && <h3>Переданы в другие воронки</h3>}
              {handed.map((funnel) => (
                <div className='lead-stats__closed' key={funnel.id}>
                  <span>{funnel.name}</span>
                  <b><Num onClick={show(funnel.name, (lead) => lead.outcome === 'handed' && lead.funnelId === funnel.id)}>{funnel.count}</Num></b>
                </div>
              ))}
            </section>
          </div>

          <div className='lead-stats__list-head'>
            <h3>Стадии по сотрудникам</h3>
            <Radio.Group size='small' value={matrixView} onChange={({target}) => setMatrixView(target.value)}>
              {MATRIX_VIEWS.map((item) => <Radio.Button key={item.key} value={item.key}>{item.title}</Radio.Button>)}
            </Radio.Group>
            {matrixView === 'percent' && (
              <Radio.Group size='small' value={matrixBase} onChange={({target}) => setMatrixBase(target.value)}>
                {MATRIX_BASES.map((item) => <Radio.Button key={item.key} value={item.key}>{item.title}</Radio.Button>)}
              </Radio.Group>
            )}
          </div>
          <div className='lead-stats__matrix'>
            <table>
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  {report.stages.map((stage) => <th key={stage.id} title={stage.name}><span>{stage.name}</span></th>)}
                </tr>
              </thead>
              <tbody>
                {report.employees.map((item) => (
                  <tr key={item.id}>
                    <th>{item.name}</th>
                    {item.reached.map((count, index) => (
                      <StageCell key={report.stages[index].id} stage={report.stages[index]} count={count} peak={peak}
                                 inPercent={inPercent(index)}
                                 onClick={show(report.stages[index].name, (lead) => lead.reached >= index, item)}
                                 whole={inPercent(index) && matrixBase === 'previous' ? item.reached[index - 1] : item.leads}/>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='lead-stats__list-head'>
            <h3>Лиды без разговора</h3>
            <Radio.Group size='small' value={listFilter} onChange={({target}) => setListFilter(target.value)}>
              {LIST_FILTERS.map((item) => <Radio.Button key={item.key} value={item.key}>{item.title}</Radio.Button>)}
            </Radio.Group>
          </div>
          <Table className='lead-stats__table' size='small' rowKey='id' columns={listColumns} dataSource={list}
                 pagination={{defaultPageSize: 25, showTotal: (total) => `Всего ${total}`}}
                 locale={{emptyText: 'Таких лидов нет'}}/>

          <Modal className='lead-stats-popup' visible={popup.visible} onCancel={() => setPopup({...popup, visible: false})} footer={null}
                 width={1040} centered destroyOnClose transitionName='lead-pop' maskTransitionName='lead-pop-mask'
                 title={<>{popup.title} <span className='lead-stats__muted'>{popup.who}, {popup.deals.length}</span></>}>
            <Table size='small' rowKey='id' columns={popupColumns(popup.extra)} dataSource={popup.deals} showSorterTooltip={false}
                   pagination={{defaultPageSize: 15, pageSizeOptions: [15, 50, 100], hideOnSinglePage: true, showTotal: (total) => `Всего ${total}`}}
                   locale={{emptyText: 'Таких сделок нет'}}/>
          </Modal>

          <p className='lead-stats__note'>
            Лид — это клиент воронки «{report.funnel.name}»: сделки с одним телефоном, созданные с разрывом меньше 30 дней, считаются одним лидом.
            Он относится к дате первой сделки, а в отчёте остаётся сделка, дошедшая дальше всех, с её {report.byFirstOwner ? 'первым ответственным (поле Битрикса «Первый ответственный за сделки»), зависшие лиды — с текущим' : 'текущим ответственным'}.
            Сделка, переданная из другой воронки, считается новой здесь с момента передачи.
            {report.repeats > 0 && ` За эти даты так объединено сделок: ${report.repeats}.`}{' '}
            Стадия считается пройденной, если сделка дошла до неё или дальше.
            В виде «Что с ними стало» лид считается там, где стоит сейчас или откуда его закрыли; «Вернули» — дошёл до этой стадии, но его вернули на более раннюю.
            В таблице стадий первая колонка всегда в штуках: от неё считаются проценты. Время идёт от появления лида в Битриксе и считается рабочее: пн–пт, 10–19.
            Контакт виден только по телефонии Битрикса: переписка в мессенджерах и звонки с личных номеров сюда не попадают.
            Разговор в течение суток до появления лида тоже считается: сделку часто создают уже после него.
            Данные есть с {report.dataFrom.split('-').reverse().join('.')}: раньше Битрикс почти два месяца не записывал звонки.
            {row.noPhone > 0 && ` Без телефона в сделке: ${row.noPhone}, контакт по ним проверить нельзя.`}
          </p>
        </Spin>
      )}
    </div>
  )
}
